/**
 * Security and data handling. Every claim on this page corresponds to something
 * in the code or the deployment config — if a control is not implemented, it is
 * not listed.
 */

import { Link } from 'react-router-dom';
import { DocSection, PublicPage } from './PublicPage.js';

export function Security(): JSX.Element {
  return (
    <PublicPage
      title="Security"
      updated="29 August 2026"
      lede="How accounts, records and backups are protected, described at the level of what is actually implemented."
    >
      <DocSection title="Accounts">
        <ul>
          <li>Passwords are hashed with scrypt and a per-account random salt, and compared in constant time. A stolen database does not yield anyone's password.</li>
          <li>Sign-in is rate limited to ten attempts per fifteen minutes per address, so a password cannot be guessed at machine speed.</li>
          <li>Sessions are random server-side tokens with a thirty-day expiry. Signing out invalidates the token on the server, not just in the browser.</li>
          <li>The session cookie is <code>HttpOnly</code>, <code>SameSite=Lax</code> and <code>Secure</code> in production, which closes the common cross-site paths to it.</li>
        </ul>
      </DocSection>

      <DocSection title="In transit">
        <p>
          HTTPS is forced at the edge; plain HTTP is redirected rather than served. The API and the
          client are the same origin, so ordinary browser use involves no cross-origin requests at
          all. The mobile shells are the exception, and they are allowed by name rather than by
          wildcard.
        </p>
      </DocSection>

      <DocSection title="At rest and in the app">
        <ul>
          <li>Every query is parameterised — the database layer takes SQL and values separately, so a name with an apostrophe is a name and not a statement.</li>
          <li>Request bodies are validated against a schema before they reach any logic, and rejected with a specific message rather than coerced.</li>
          <li>What a role can see is enforced on the server for every request. Hiding a screen in the client is not treated as access control.</li>
          <li>An audit log records who changed what, so a disputed record has a history.</li>
        </ul>
      </DocSection>

      <DocSection title="Backups and recovery">
        <p>
          The database runs in WAL mode and, where a replica is configured, every committed write is
          in object storage within about ten seconds. A machine that dies loses seconds of work
          rather than a day, and a fresh machine restores from the replica before the server starts —
          so replacing the disk is not the same as losing the school.
        </p>
        <p>
          Thirty days of restore points are kept. The consequence for deleted data is spelled out on
          the <Link to="/privacy">privacy page</Link>.
        </p>
      </DocSection>

      <DocSection title="Architecture, and what it costs">
        <p>
          FlightSlots deliberately runs as one machine with one disk. SQLite has a single writer and
          no way to share a file between machines, so a second instance would quietly get its own
          copy of the school — which is a worse failure than being down. The trade is real and worth
          knowing: a deploy is a short restart rather than a rolling one, and there is no automatic
          failover to a second region.
        </p>
      </DocSection>

      <DocSection title="What we do not claim">
        <p>
          There is no third-party audit, SOC 2 report or penetration test behind this page, and we
          are not going to imply one. This is a description of the controls that exist, so that you
          can judge them.
        </p>
      </DocSection>

      <DocSection title="Reporting a vulnerability">
        <p>
          If you find something, tell us before you tell anyone else and we will not come after you
          for looking. Use the <Link to="/contact?about=support">contact form</Link>, say
          &ldquo;security&rdquo; in the first line, and include what you did and what you saw. We
          acknowledge within one business day.
        </p>
        <p>
          Please do not test against a real school's data, run automated scans that degrade the
          service for other people, or access records that are not yours. Set up a free account and
          test against that instead.
        </p>
      </DocSection>
    </PublicPage>
  );
}
